import moment from "moment";

/**
 * Shared data shaping for the dashboard call-average charts.
 *
 * Both cards get the same shape back from the API - one row per (day, series,
 * calls) - so the day grouping, the summary figures and the time bucketing are
 * the same job in both places and live here rather than in each chart.
 */

export const DATE_KEY = "D MMM, YY";

export const EMPTY_SUMMARY = {
  totalCalls: 0,
  activeDays: 0,
  rangeLabel: null,
};

/**
 * Totals for the summary strip, taken from the raw daily rows before any
 * bucketing, so the figures describe the employee rather than the resolution
 * the chart happens to be drawn at.
 *
 * `days` is calendar days across the range, not the number of rows the API
 * returned - reporting "11 of 11 days active" for eleven calls spread over
 * eighteen months told the reader the opposite of the truth.
 */
export function summarise(rows, products) {
  let totalCalls = 0;
  let activeDays = 0;
  rows.forEach((row) => {
    const dayTotal = products.reduce((sum, p) => sum + (row[p] || 0), 0);
    totalCalls += dayTotal;
    if (dayTotal > 0) {
      activeDays += 1;
    }
  });
  const first = rows.length ? moment(rows[0].date, DATE_KEY) : null;
  const last = rows.length ? moment(rows[rows.length - 1].date, DATE_KEY) : null;
  return {
    totalCalls,
    activeDays,
    rangeLabel:
      first && last && first.isValid() && last.isValid()
        ? first.isSame(last, "day")
          ? first.format("D MMM YYYY")
          : `${first.format("D MMM YYYY")} – ${last.format("D MMM YYYY")}`
        : null,
  };
}

/**
 * Orders the series by how much each product actually carries, so the busiest
 * take the leading palette slots and the legend reads busiest-first.
 */
export function rankSeries(rows, series) {
  return series
    .map((product) => [
      product,
      rows.reduce((sum, row) => sum + (row[product] || 0), 0),
    ])
    .sort((a, b) => b[1] - a[1])
    .map(([product]) => product);
}

/**
 * Puts a real calendar on the x axis.
 *
 * The API only returns days with activity, and those days were drawn as
 * neighbours - so three calls in March and one the following August came out
 * evenly spaced, as though they were consecutive. Bucketing into contiguous
 * days, weeks or months (whichever the range can show) restores the spacing
 * and keeps the number of marks readable. Totals are unchanged: a month's bar
 * is exactly the calls the API reported inside it.
 */
export function bucketByTime(rows, series, forcedUnit) {
  if (!rows.length) {
    return { rows, unit: forcedUnit || "day" };
  }
  const first = moment(rows[0].date, DATE_KEY);
  const last = moment(rows[rows.length - 1].date, DATE_KEY);
  if (!first.isValid() || !last.isValid()) {
    return { rows, unit: forcedUnit || "day" };
  }

  const span = last.diff(first, "days");
  /* Auto picks a resolution the card can actually draw; an explicit choice
     from the granularity control always wins. */
  const unit =
    forcedUnit || (span <= 45 ? "day" : span <= 400 ? "week" : "month");
  const startOf = unit === "week" ? "isoWeek" : unit;
  const tickFormat =
    unit === "year"
      ? "YYYY"
      : unit === "month"
      ? "MMM YY"
      : span > 330
      ? "D MMM YY"
      : "D MMM";

  const buckets = new Map();
  rows.forEach((row) => {
    const key = moment(row.date, DATE_KEY).startOf(startOf).format("YYYY-MM-DD");
    const bucket = buckets.get(key) || {};
    series.forEach((product) => {
      bucket[product] = (bucket[product] || 0) + (row[product] || 0);
    });
    buckets.set(key, bucket);
  });

  const out = [];
  const cursor = first.clone().startOf(startOf);
  const stop = last.clone().startOf(startOf);
  while (cursor.isSameOrBefore(stop)) {
    const bucket = buckets.get(cursor.format("YYYY-MM-DD"));
    const row = {
      date: cursor.format(tickFormat),
      fullLabel: bucketLabel(cursor, unit),
    };
    series.forEach((product) => {
      row[product] = bucket ? bucket[product] || 0 : 0;
    });
    out.push(row);
    cursor.add(1, unit);
  }

  return { rows: out, unit };
}

export function bucketLabel(start, unit) {
  if (unit === "year") {
    return start.format("YYYY");
  }
  if (unit === "month") {
    return start.format("MMMM YYYY");
  }
  if (unit === "week") {
    return `${start.format("D MMM")} – ${start
      .clone()
      .add(6, "days")
      .format("D MMM YYYY")}`;
  }
  return start.format("D MMM YYYY");
}
